import { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog, Skeleton, cn } from '@eombor/ui';
import { rackHeightM, rackWidthM, type RackKind, type RackShape } from '@eombor/contracts';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { SceneBoundary } from '../../warehouse-3d/ui/SceneBoundary.js';
import type { DraftRack, DraftZone } from '../model/builder.store.js';
import {
  placeInZone,
  rackAabb,
  rackWithinZone,
  rectsOverlap,
  type WarehouseFloor,
} from '../model/plan-geometry.js';
import { RackEditor, type RackFormValues } from './RackEditor.js';

/**
 * STELLAJ OYNASI — qo'shish ham, tahrirlash ham.
 *
 * O'LCHAM FAQAT SHU YERDA beriladi va bu qat'iy qoida. Ilgari o'lchamni
 * ikki joyda berish mumkin edi: oynada raqam bilan va rejada burchakni
 * sudrab. Ikkinchisi birinchisini jimgina buzardi — oynada "8 proyom ×
 * 150 sm = 12 m" yozilgan javon rejada bir tortishda 2 m bo'lib qolardi,
 * chunki sudrash proyomlarni qayta o'lchab yuborardi. Endi rejada faqat
 * KO'CHIRISH bor.
 *
 * Tepada javonning O'ZI turadi va har terilgan raqamda o'zgaradi:
 * "8 proyom × 150 sm" degan yozuv ko'z oldida javon hosil qilmaydi,
 * javonning tasviri esa qiladi.
 *
 * JOYNI DASTUR TOPADI. Odam zonani va yo'nalishni (bo'yiga/eniga)
 * tanlaydi, joyni `placeInZone` o'sha zona ichidan qidiradi. Tahrirlashda
 * javon O'Z JOYIDA qoladi — agar yangi o'lcham u yerga hali ham sig'sa.
 */

/* `three` ~900 kB: u faqat usta ochilganda va faqat WebGL bo'lsa yuklanadi. */
const RackPreview3d = lazy(async () => ({
  default: (await import('../../warehouse-3d/ui/RackPreview3d.js')).RackPreview3d,
}));

export interface RackDialogProps {
  open: boolean;
  onClose: () => void;
  zones: DraftZone[];
  racks: DraftRack[];
  floor: WarehouseFloor;
  /** `null` — yangi javon; aks holda shu javon tahrirlanadi. */
  rack?: DraftRack | null;
  /** Boshlang'ich zona — rejada tanlab turilgani. */
  defaultZoneId?: string | null;
  onSubmit: (input: { zoneId: string; values: RackFormValues; x: number; y: number }) => void;
}

/**
 * Ustadagi bo'linish BITTA ro'yxat, shartnoma esa har qator uchun
 * alohida kutadi — bu yerda yoyiladi.
 *
 * Javon YASALAYOTGANDA qatorlarni bir-biridan ajratish ortiqcha ish:
 * odam avval javonni qo'yadi, keyin kerakli taxtaga ajratgich
 * qo'shadi. Ajratish katak kartochkasida.
 */
const rowsOf = (values: RackFormValues): number[][] =>
  values.levelHeights.map(() => [...values.bays]);

/** Yangi javonga taklif qilinadigan kod — zonadagi eng kattasidan keyingisi. */
const nextCode = (racks: readonly DraftRack[], zoneId: string): string => {
  const used = racks.filter((rack) => rack.zoneId === zoneId).map((rack) => Number(rack.code));
  const highest = used.reduce((max, value) => (Number.isFinite(value) ? Math.max(max, value) : max), 0);
  return String(highest + 1).padStart(2, '0');
};

export const RackDialog = ({
  open,
  onClose,
  zones,
  racks,
  floor,
  rack = null,
  defaultZoneId = null,
  onSubmit,
}: RackDialogProps) => {
  const [zoneId, setZoneId] = useState<string>(
    rack?.zoneId ?? defaultZoneId ?? zones[0]?.id ?? '',
  );
  const [preview, setPreview] = useState<RackFormValues | null>(null);

  /* Ko'rinish va o'lcham yozuvi BITTA manbadan oziqlanadi: ikkovi
     ajralsa, chizma bir narsani, yozuv boshqasini ko'rsatib qolardi. */
  const shape: RackShape & { kind: RackKind } = useMemo(
    () =>
      (preview === null ? null : { ...preview, bays: rowsOf(preview) }) ??
      // `RackEditor` dagi DEFAULTS bilan bir xil: birinchi kadrda chizma
      // formadan boshqa o'lcham ko'rsatib turmasin.
      rack ?? {
        kind: 'RACK',
        depthM: 1.1,
        bays: [[2], [2], [2], [2]],
        levelHeights: [0.5, 0.5, 0.5, 0.5],
      },
    [preview, rack],
  );

  const zone = zones.find((candidate) => candidate.id === zoneId) ?? zones[0] ?? null;

  /* Sarlavha ham, o'lcham yozuvi ham formadagi TURDAN kelib chiqadi:
     «stellaj qo'shish» deb turgan oynada maydon yasalayotgan bo'lsa,
     yozuv o'zi yolg'on gapirardi. */
  const isFloor = shape.kind === 'FLOOR';

  /*
   * Joy zona tanlanganda ham, o'lcham o'zgarganda ham qayta hisoblanadi:
   * kengaytirilgan javon eskisi sig'gan joyga sig'masligi mumkin.
   *
   * TAHRIRLASHDA javon O'Z JOYIDA qoladi — agar u yerga hali ham sig'sa.
   * Aks holda odam bitta raqamni tuzatgani uchun javon rejaning butunlay
   * boshqa burchagiga sakrab ketardi.
   */
  const spot = useMemo(() => {
    if (zone === null || preview === null) return null;

    const others = racks.filter((other) => other.id !== rack?.id);
    if (rack !== null && rack.zoneId === zone.id) {
      const stays = {
        pos: { ...rack.pos, rotationDeg: preview.rotationDeg },
        bays: rowsOf(preview),
        depthM: preview.depthM,
      };
      const clash = others.some((other) => rectsOverlap(rackAabb(other), rackAabb(stays)));
      if (!clash && rackWithinZone(stays, zone.plan)) return { x: rack.pos.x, y: rack.pos.y };
    }
    return placeInZone(zone.plan, { ...preview, bays: rowsOf(preview) }, others, floor, preview.rotationDeg);
  }, [zone, preview, racks, floor, rack]);

  const handleChange = useCallback((values: RackFormValues) => setPreview(values), []);

  const handleSave = (values: RackFormValues): void => {
    if (zone === null || spot === null) return;
    onSubmit({ zoneId: zone.id, values, x: spot.x, y: spot.y });
    setPreview(null);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        isFloor
          ? rack === null
            ? t('builder.newFloorTitle')
            : t('builder.editFloorTitle')
          : rack === null
            ? t('builder.newRackTitle')
            : t('builder.editRackTitle')
      }
      size="lg"
    >
      <div className="flex flex-col gap-4">
        {/* Javonning o'zi — tepada, raqamlardan OLDIN. */}
        <SceneBoundary fallback={<div className="hidden" />}>
          <Suspense fallback={<Skeleton className="h-[14rem] w-full" />}>
            <RackPreview3d
              rack={shape}
              className="h-[14rem] w-full overflow-hidden rounded-[var(--radius-sm)]"
            />
          </Suspense>
        </SceneBoundary>

        {/*
          O'LCHAM YOZUVI — chizmaning o'zi "bu haqiqatan 200 sm mi?" degan
          savolga javob bermaydi: sahnada solishtiradigan narsa yo'q va
          baland javon ingichka bo'lib ko'rinadi. Raqam esa shubhani
          butunlay yopadi.

          `aria-hidden` EMAS: chizma ekran o'quvchi uchun ko'rinmaydi va
          bu yozuv uning yagona muqobili.
        */}
        <p className="tabular text-text-muted text-center text-xs">
          {isFloor
            ? t('builder.previewSummaryFloor', {
                len: Math.round(rackWidthM(shape) / 0.01),
                depth: Math.round(shape.depthM / 0.01),
                height: Math.round(rackHeightM(shape) / 0.01),
                spots: shape.bays[0]?.length ?? 0,
              })
            : t('builder.previewSummary', {
                len: Math.round(rackWidthM(shape) / 0.01),
                depth: Math.round(shape.depthM / 0.01),
                height: Math.round(rackHeightM(shape) / 0.01),
                bays: shape.bays.length,
                levels: shape.levelHeights.length,
                cells: shape.bays.length * shape.levelHeights.length,
              })}
        </p>

        <Select
          label={t('builder.newRackZone')}
          value={zoneId}
          options={zones.map((item) => ({ value: item.id, label: `${item.code} — ${item.name}` }))}
          onChange={(event) => setZoneId(event.target.value)}
        />

        {/* Sig'masligi TERISH PAYTIDA aytiladi, "Qo'shish" bosilgandan keyin
            emas: odam raqamni qayta o'ylashi kerak bo'lsa, buni hozir bilsin. */}
        {preview !== null && spot === null ? (
          <p
            role="alert"
            className={cn(
              'border-warn/25 flex items-start gap-2 rounded-[var(--radius-sm)] border',
              'bg-warn-soft text-warn px-3 py-2 text-sm',
            )}
          >
            <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0" />
            {t('builder.newRackNoRoom', { zone: zone?.name ?? '' })}
          </p>
        ) : null}

        <RackEditor
          rack={rack}
          submitLabel={
            rack === null
              ? isFloor
                ? t('builder.newFloorSubmit')
                : t('builder.newRackSubmit')
              : t('builder.save')
          }
          defaultCode={nextCode(racks, zoneId)}
          onChange={handleChange}
          onSave={handleSave}
          onCancel={onClose}
          key={rack?.id ?? `new:${zoneId}`}
        />
      </div>
    </Dialog>
  );
};
